import { Button, ConfigProvider, Flex, Modal, Typography } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { Fragment } from "react";
import {
  ModalHeader,
  modalButtonsTheme,
  modalSubtitleStyle,
} from "components/modals/ModalChrome";
import { translate, translateWithVars } from "shared/languages";
import { colors } from "shared/theme/color";
import { formatPhone } from "shared/utils/phone";
import { textPx } from "shared/constants/zoom";

export interface CsvErrorRow {
  rowNumber: number;
  errorMessage: string;
}

// Renders the CSV import's 422 per-row error report (server
// UploadCsvValidationError details). Static modal so the mutation hook can
// open it without JSX context.
export const showCsvErrorModal = (details: CsvErrorRow[]) => {
  Modal.error({
    title: translate("notifications.missing_csv.title"),
    maskClosable: true,
    width: 560,
    // Static modals bypass the ConfigProvider button theme; keep the navy
    // primary by hand.
    okButtonProps: { style: { background: colors.navy } },
    content: (
      <div>
        <Typography.Text style={{ color: colors.ink2 }}>
          {details.length} {translate("csv_import.failed_count_suffix")}
        </Typography.Text>
        <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 12 }}>
          {details.map((row, index) => (
            <div
              key={`${row.rowNumber}-${index}`}
              style={{
                padding: "8px 0",
                borderTop: index === 0 ? "none" : `1px solid ${colors.line}`,
                fontSize: textPx(13),
              }}
            >
              <Typography.Text strong style={{ marginRight: 8 }}>
                {translate("csv_import.row_label")} {row.rowNumber}
              </Typography.Text>
              <Typography.Text style={{ color: colors.ink2 }}>
                {row.errorMessage}
              </Typography.Text>
            </div>
          ))}
        </div>
      </div>
    ),
  });
};

export interface CsvReactivatedClient {
  full_name: string;
  phone_number: string | null;
}

// A Klien import that names someone this studio had deactivated reactivates
// them (the server says who in `meta.reactivated`). Tambah Klien asks before
// doing that; a file can't, so it is reported afterwards instead of
// happening silently.
export const showCsvReactivatedModal = (clients: CsvReactivatedClient[]) => {
  Modal.info({
    title: translate("csv_import.reactivated_title"),
    maskClosable: true,
    width: 480,
    okButtonProps: { style: { background: colors.navy } },
    content: (
      <div>
        <Typography.Text style={{ color: colors.ink2 }}>
          {translateWithVars("csv_import.reactivated_body", { n: clients.length })}
        </Typography.Text>
        <div style={{ maxHeight: 320, overflowY: "auto", marginTop: 12 }}>
          {clients.map((c, index) => (
            <div
              key={`${c.phone_number}-${index}`}
              style={{
                padding: "8px 0",
                borderTop: index === 0 ? "none" : `1px solid ${colors.line}`,
                fontSize: textPx(13),
              }}
            >
              <Typography.Text strong style={{ marginRight: 8 }}>
                {c.full_name}
              </Typography.Text>
              {c.phone_number && (
                <Typography.Text style={{ color: colors.ink2 }}>
                  {formatPhone(c.phone_number)}
                </Typography.Text>
              )}
            </div>
          ))}
        </div>
      </div>
    ),
  });
};

/** One row the Klien import thinks may be a double entry (server 409). */
export interface CsvDuplicateRow {
  row_number: number;
  client_name: string;
  phone_number: string;
  package_name: string;
  unlimited: boolean;
  remaining_credits: number | null;
  /** Jakarta YYYY-MM-DD, null when the row left tanggal_pembelian blank. */
  purchase_date: string | null;
  expiry_date: string | null;
  match:
    | { kind: "existing"; purchase_date: string }
    | { kind: "file"; row_number: number };
}

// More than this and the list stops helping: the admin should look at the
// file instead, and the rest is counted under the list.
const DUPLICATE_LIST_CAP = 20;

// The server sends Jakarta calendar days; formatting them as-is keeps them
// from shifting a day in a browser outside Jakarta.
const dayLabel = (day: string) => day.split("-").reverse().join("/");

const tnum = { fontVariantNumeric: "tabular-nums" } as const;

const DuplicateItem = ({ row, first }: { row: CsvDuplicateRow; first: boolean }) => {
  const facts = [
    row.unlimited
      ? translate("csv_import.duplicates_membership")
      : translateWithVars(
          row.remaining_credits === 1
            ? "csv_import.duplicates_credits_one"
            : "csv_import.duplicates_credits_other",
          { n: row.remaining_credits ?? 0 },
        ),
    row.purchase_date &&
      translateWithVars("csv_import.duplicates_bought", { date: dayLabel(row.purchase_date) }),
    row.expiry_date &&
      translateWithVars("csv_import.duplicates_ends", { date: dayLabel(row.expiry_date) }),
  ].filter(Boolean);
  const match =
    row.match.kind === "existing"
      ? translateWithVars("csv_import.duplicates_match_existing", {
          date: dayLabel(row.match.purchase_date),
        })
      : translateWithVars("csv_import.duplicates_match_file", {
          row: row.match.row_number,
        });
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: "auto minmax(0, 1fr)",
        columnGap: 12,
        padding: "12px 14px",
        borderTop: first ? "none" : `1px solid ${colors.line}`,
      }}
    >
      {/* The row number the admin looks up in the file. */}
      <span
        style={{
          ...tnum,
          alignSelf: "start",
          marginTop: 1,
          padding: "2px 8px",
          borderRadius: 999,
          background: colors.bgSoft,
          color: colors.ink2,
          fontSize: textPx(12),
          fontWeight: 600,
          whiteSpace: "nowrap",
        }}
      >
        {translate("csv_import.row_label")} {row.row_number}
      </span>
      <div style={{ minWidth: 0, display: "grid", rowGap: 3 }}>
        {/* Name and phone wrap as two units, never inside the number. */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "baseline", columnGap: 8 }}>
          <span style={{ fontWeight: 600, color: colors.ink, fontSize: textPx(13.5), overflowWrap: "anywhere" }}>
            {row.client_name}
          </span>
          <span style={{ ...tnum, color: colors.ink2, fontSize: textPx(12.5), whiteSpace: "nowrap" }}>
            {formatPhone(row.phone_number)}
          </span>
        </div>
        <div style={{ color: colors.ink, fontSize: textPx(13), overflowWrap: "anywhere" }}>
          {row.package_name}
        </div>
        {/* Each fact stays on one line; a narrow screen wraps between them. */}
        <div style={{ ...tnum, color: colors.ink2, fontSize: textPx(12.5) }}>
          {/* The separator sits OUTSIDE the nowrap spans: it is the only
              place a line may break, and without it the whole line becomes
              one unbreakable run that widens the pop-up. */}
          {facts.map((fact, index) => (
            <Fragment key={index}>
              {index > 0 && " · "}
              <span style={{ whiteSpace: "nowrap" }}>{fact}</span>
            </Fragment>
          ))}
        </div>
        {/* What it matches, in words on a tint (never colour alone). */}
        <div
          style={{
            justifySelf: "start",
            marginTop: 3,
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "3px 8px",
            borderRadius: 6,
            background: colors.amberTint,
            color: colors.ink,
            fontSize: textPx(12.5),
            ...tnum,
          }}
        >
          <CopyOutlined aria-hidden style={{ color: colors.amberDark }} />
          {match}
        </div>
      </div>
    </li>
  );
};

// The Klien import answers 409 with the rows that look like a package the
// client already holds, or like an earlier row of the same file. Nothing is
// written yet: Impor Tetap re-sends the file with the override, Batal stops.
// Batal takes the focus so Enter never imports by accident.
export const showCsvDuplicateModal = (
  duplicates: CsvDuplicateRow[],
  onConfirm: () => void,
) => {
  const shown = duplicates.slice(0, DUPLICATE_LIST_CAP);
  const more = duplicates.length - shown.length;
  const dismiss = () => Modal.destroyAll();
  Modal.warning({
    icon: null,
    width: "min(620px, calc(100vw - 32px))",
    title: <ModalHeader tone="amber" title={translate("csv_import.duplicates_title")} />,
    content: (
      <>
        <Typography.Text style={modalSubtitleStyle}>
          {translateWithVars(
            duplicates.length === 1
              ? "csv_import.duplicates_body_one"
              : "csv_import.duplicates_body_other",
            { n: duplicates.length },
          )}
        </Typography.Text>
        <ul
          aria-label={translate("csv_import.duplicates_title")}
          style={{
            listStyle: "none",
            margin: "14px 0 0",
            padding: 0,
            border: `1px solid ${colors.line}`,
            borderRadius: 8,
            maxHeight: 340,
            overflowY: "auto",
          }}
        >
          {shown.map((row, index) => (
            <DuplicateItem key={`${row.row_number}-${index}`} row={row} first={index === 0} />
          ))}
        </ul>
        {more > 0 && (
          <Typography.Text style={{ display: "block", marginTop: 8, color: colors.ink2, fontSize: textPx(12.5) }}>
            {translateWithVars(
              more === 1 ? "csv_import.duplicates_more_one" : "csv_import.duplicates_more_other",
              { n: more },
            )}
          </Typography.Text>
        )}
      </>
    ),
    closable: true,
    maskClosable: true,
    footer: (
      <ConfigProvider theme={modalButtonsTheme}>
        <Flex justify="flex-end" gap={10} wrap="wrap" style={{ marginTop: 22 }}>
          <Button autoFocus onClick={dismiss}>
            {translate("button.cancel")}
          </Button>
          <Button
            type="primary"
            onClick={() => {
              onConfirm();
              Modal.destroyAll();
            }}
          >
            {translate("csv_import.duplicates_confirm")}
          </Button>
        </Flex>
      </ConfigProvider>
    ),
    onCancel: dismiss,
  });
};
